"use client";

import React from "react";
import type {
  TabConfig,
  TabContentProps,
} from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Time_Entries } from "@/src/gql/graphql";

// Import time entry detail config
import { getEntityDetailConfig } from "@/lib/entity-registry";

// Import the real time-entry detail tab components (same map the detail PAGE uses)
import { TimeEntryDetailTabComponents } from "@/features/time-entries/components/TimeEntryDetailTabComponents";

// Generic resolution by `customComponent` key (NOT inline placeholder JSX) so
// the modal renders the SAME real tab components as the detail page, and a tab
// declared in the detail config can never silently fall through.
const TIME_ENTRY_TAB_COMPONENTS = TimeEntryDetailTabComponents as Record<
  string,
  React.ComponentType<TabContentProps<Time_Entries>>
>;

function resolveTimeEntryTabComponent(
  tab: TabConfig<Time_Entries> | undefined
): React.ComponentType<TabContentProps<Time_Entries>> {
  const custom = tab?.content?.customComponent;
  if (typeof custom === "string" && TIME_ENTRY_TAB_COMPONENTS[custom]) {
    return TIME_ENTRY_TAB_COMPONENTS[custom];
  }
  return TIME_ENTRY_TAB_COMPONENTS.TimeEntryDetails;
}

// ============================================================================
// TIME ENTRY DETAIL MODAL TABS HOOK
// ============================================================================

export interface UseTimeEntryDetailModalTabsReturn {
  tabs: TabConfig<Time_Entries>[];
  renderPrimaryArea: (activeTab: string, timeEntry: Time_Entries) => React.ReactNode;
  renderInfoPanel: (timeEntry: Time_Entries) => React.ReactNode;
}

export function useTimeEntryDetailModalTabs(): UseTimeEntryDetailModalTabsReturn {
  // ✅ CRITICAL: Pull ALL tabs from the detail config (just like contacts does)
  // This ensures all tabs from the detail page are available in the modal:
  // - details, ticket, contact, activity
  const tabs = getEntityDetailConfig<Time_Entries>("time_entries").tabs;

  // Render primary area based on active tab
  const renderPrimaryArea = (activeTab: string, timeEntry: Time_Entries): React.ReactNode => {
    const tabConfig = tabs.find((t) => t.id === activeTab) ?? tabs[0];
    const TabComponent = resolveTimeEntryTabComponent(tabConfig);
    return <TabComponent item={timeEntry} tab={tabConfig} />;
  };

  // Render info panel (stable across tabs)
  const renderInfoPanel = (timeEntry: Time_Entries): React.ReactNode => {
    return (
      <div className="space-y-4">
        <div className="border rounded-lg p-4 bg-white">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Time Entry Information</h3>
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Date Worked:</span>
              <p className="text-gray-900 font-medium">{timeEntry.date_worked}</p>
            </div>
            {timeEntry.hours && (
              <div>
                <span className="text-gray-600">Hours:</span>
                <p className="text-gray-900">{timeEntry.hours}</p>
              </div>
            )}
            {timeEntry.entry_type && (
              <div>
                <span className="text-gray-600">Entry Type:</span>
                <p className="text-gray-900">{timeEntry.entry_type}</p>
              </div>
            )}
            {timeEntry.billable_rate && (
              <div>
                <span className="text-gray-600">Billable Rate:</span>
                <p className="text-gray-900">${timeEntry.billable_rate}/h</p>
              </div>
            )}
            {timeEntry.is_approved !== undefined && (
              <div>
                <span className="text-gray-600">Status:</span>
                <p className="text-gray-900">{timeEntry.is_approved ? "Approved" : "Pending"}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return {
    tabs,
    renderPrimaryArea,
    renderInfoPanel,
  };
}
